import type { Context } from '../integrations/context-types';
import type { Agent, ConversationRef } from '../types/transport';

export type PaneId = string;

export type LayoutMode = 'sidebar' | 'fullscreen' | 'floating';

export interface PaneViewState {
  agentId: string | null;
  conversationId: string | null;
  composerText: string;
  toneId: string | null;
  /** Composer "Web search" switch. On by default; the host only
   *  shows the control when the tenant has enabled web search. */
  webSearchEnabled: boolean;
  scrollPositionPx: number;
  nextPrompt: NextPromptState;
}

/** The guessed next prompt for this pane, shown as the composer placeholder.
 *  `requestId` names the request in flight; a response whose id no longer
 *  matches (the user typed, sent, or dismissed meanwhile) is dropped. The
 *  suggestion carries its conversation so it only shows in the conversation
 *  it was made for. */
export interface NextPromptState {
  requestId: string | null;
  suggestion: NextPromptSuggestion | null;
}

export interface NextPromptSuggestion {
  conversationId: string;
  text: string;
}

export interface StreamError {
  message: string;
  /** ISO timestamp when the error was recorded. */
  at: string;
}

export interface ShellState {
  agents: {
    byId: Record<string, Agent>;
    allIds: string[];
    status: 'idle' | 'loading' | 'ready' | 'error';
    defaultAgentId: string | null;
  };
  conversations: {
    byId: Record<string, ConversationRef>;
    byAgentId: Record<string, string[]>;
  };
  selection: {
    agentId: string | null;
    conversationId: string | null;
    /** Bumped by every action that resets `conversationId` to `null`
     *  (`AGENT_SELECTED`, `CONVERSATION_NEW_REQUESTED`). `conversationId`
     *  alone can't signal a reset when it was already `null` — e.g. the
     *  user stopped a response before the server assigned an id — so
     *  consumers that need to detect "a reset just happened" (clearing a
     *  stale, never-bound `useChat` session) watch this instead. */
    resetNonce: number;
  };
  drawer: {
    isOpen: boolean;
    view: 'default' | 'all-agents' | 'all-conversations';
    pinned: boolean;
  };
  launcher: {
    isOpen: boolean;
    panes: Record<PaneId, PaneViewState>;
  };
  contexts: {
    user: Context[];
    derived: Context[];
    /** The list the host last passed to `CONTEXT_DERIVED_SET`, before
     *  dismissals are filtered out. Lets a dismissed chip be offered
     *  back (and re-attached) without the host re-deriving it. */
    derivedOffered: Context[];
    /** Last routeSignature passed to `CONTEXT_DERIVED_SET`. Used by
     *  `CONTEXT_DERIVED_REMOVED` so the dismissal gets bucketed under
     *  the right route entry. null when the host hasn't supplied a
     *  signature yet — legacy / call-sites that omit it. */
    lastDerivedRouteSignature: string | null;
    /** Per-route memory of dismissed derived-chip fingerprints. When
     *  `CONTEXT_DERIVED_SET` runs with a routeSignature whose bucket is
     *  non-empty, the matching fingerprints are filtered out so the
     *  user's dismissal sticks for that route. Cleared only when the
     *  user explicitly re-attaches (`CONTEXT_DERIVED_RESTORED`, or
     *  `CONTEXT_DERIVED_PINNED` on a route re-emit) — navigating away
     *  leaves the bucket in place, and there is no time-based eviction. */
    derivedDismissed: Record<string, string[]>;
  };
  /** Per-conversation stream errors. Key is the conversationId, or 'fresh'
   *  for the not-yet-created-conversation slot. */
  errors: Record<string, StreamError>;
  /** Flips to `true` once `useStorageHydration` has run phase 1 (reading
   *  the session-stored agentId and persisted pinned contexts). Hosts
   *  gate any auto-select effect on this so a default-agent dispatch
   *  doesn't race the rehydrated selection. Also flips immediately when
   *  no storage adapter is configured — the host's auto-select can then
   *  run unblocked. */
  storageHydrated: boolean;
  /** Queued user message that ChatPanel auto-submits to the currently
   *  selected agent. Populated by `selectAgent(id, replayQuery)` during
   *  specialist-switch navigation cards — the original question carries
   *  to the newly selected agent so they answer it without the user
   *  retyping. Null when no replay is pending. */
  pendingReplay: string | null;
  layoutMode: LayoutMode;
}

export const initialShellState: ShellState = {
  agents: { byId: {}, allIds: [], status: 'idle', defaultAgentId: null },
  conversations: { byId: {}, byAgentId: {} },
  selection: { agentId: null, conversationId: null, resetNonce: 0 },
  drawer: { isOpen: false, view: 'default', pinned: false },
  launcher: {
    isOpen: false,
    panes: {
      main: {
        agentId: null,
        conversationId: null,
        composerText: '',
        toneId: null,
        webSearchEnabled: true,
        scrollPositionPx: 0,
        nextPrompt: { requestId: null, suggestion: null },
      },
    },
  },
  contexts: {
    user: [],
    derived: [],
    derivedOffered: [],
    lastDerivedRouteSignature: null,
    derivedDismissed: {},
  },
  errors: {},
  storageHydrated: false,
  pendingReplay: null,
  layoutMode: 'sidebar',
};
